import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RichTextDoc } from '@avslack/shared';
import { invokeCommand, listCommands, type CommandMention } from '../lib/api';
import { pushEphemeral } from './ephemeral';

/**
 * A szerkeszto emlitesei a parancshoz.
 *
 * A `/remind @Kis Anna holnap 9-kor` szovegebol a szokozos nevet nem lehetne
 * biztosan kiolvasni - ezert a parancs mellett a valodi emliteseket is
 * elkuldjuk, azonositoval.
 */
function mentionsOf(doc: RichTextDoc | undefined): CommandMention[] {
  const found: CommandMention[] = [];
  const walk = (node: { type?: string; attrs?: Record<string, unknown>; content?: unknown[] }) => {
    if (node.type === 'mention') {
      const kind = node.attrs?.kind;
      if ((kind === 'user' || kind === 'group') && node.attrs?.id && node.attrs.label) {
        found.push({ kind, id: String(node.attrs.id), label: String(node.attrs.label) });
      }
    }
    for (const child of node.content ?? []) walk(child as typeof node);
  };
  if (doc) walk(doc as unknown as Parameters<typeof walk>[0]);
  return found;
}

/**
 * Slash parancsok a szerkesztosavban.
 *
 * A legfontosabb szabaly itt: a `/` kezdetu sor CSAK akkor parancs, ha
 * tenyleg letezik ilyen nevu parancs. Kulonben rendes uzenetkent megy el -
 * egy elgepelt "/holnap talalkozunk" nem tunhet el csendben.
 */
export function useSlashCommands(channelId: string) {
  const queryClient = useQueryClient();
  const { data: commands } = useQuery({
    queryKey: ['commands'],
    queryFn: listCommands,
    staleTime: 5 * 60_000,
  });

  const run = useCallback(
    async (text: string, allowed: boolean, body?: RichTextDoc): Promise<boolean> => {
      // Fajlmelleklettel egyutt nem parancs - ott az uzenet a lenyeg.
      if (!allowed) return false;

      const match = /^\/([a-z0-9][a-z0-9_-]{0,30})(?:\s|$)/i.exec(text.trim());
      const name = match?.[1]?.toLowerCase();
      if (!name) return false;
      if (!commands?.some((command) => command.command === name)) return false;

      try {
        const result = await invokeCommand(channelId, text.trim(), mentionsOf(body));
        // A /remind uj emlekeztetoje a listaban is latsszon.
        if (result.reminderId) void queryClient.invalidateQueries({ queryKey: ['reminders'] });
        if (result.pending) {
          // A kulso rendszer nem valaszolt idoben. Ezt megmondjuk, nem
          // hagyjuk a felhasznalot talalgatni.
          pushEphemeral(channelId, `/${name}`, 'Elküldve. A válasz hamarosan megérkezik.');
        }
      } catch (error) {
        pushEphemeral(
          channelId,
          `/${name}`,
          error instanceof Error ? error.message : 'A parancs nem futott le.',
        );
      }

      return true;
    },
    [channelId, commands, queryClient],
  );

  return { commands: commands ?? [], run };
}
